/**
 * CycleOnboardingModal — iter1094
 *
 * 3-стъпков модал показан веднъж на новорегистрирани жени. Събира:
 *   1. LMP (последна менструация) — за derive-ване на фазата
 *   2. Средна дължина на цикъла — прецизност
 *   3. Текуща фаза (или skip = auto-derived)
 *
 * Отключва Hormonal twin от старт без потребителят да търси къде да го въведе.
 * Използва PUT `/api/dil/cycle` (single endpoint за LMP + length + phase).
 *
 * Показва се само ако:
 *   • gender = female (или „жена")
 *   • няма записан LMP в профила
 *   • не е dismiss-нат в localStorage
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, ArrowRight, ArrowLeft, Check, Loader2, X } from "lucide-react";
import { deriveCyclePhase } from "@/lib/profileCompleteness";

const STORAGE_KEY = "nutramilo_cycle_onboarding_dismissed";

const PHASES = [
  { key: "menstrual", emoji: "🩸", labelBg: "Менструална", labelEn: "Menstrual", color: "rose" },
  { key: "follicular", emoji: "🌱", labelBg: "Фоликуларна", labelEn: "Follicular", color: "emerald" },
  { key: "ovulatory", emoji: "☀️", labelBg: "Овулаторна", labelEn: "Ovulatory", color: "amber" },
  { key: "luteal", emoji: "🌙", labelBg: "Лутеална", labelEn: "Luteal", color: "violet" },
];

/**
 * @param {boolean} open
 * @param {(v:boolean)=>void} onOpenChange
 * @param {() => void} [onComplete] — iter1095: optional callback след успешен submit.
 *   Ако е зададен, отменя default navigate към /twin-network.
 * @param {React.ReactNode} [stepperSlot] — iter1096: optional slot за external
 *   progress stepper (Register контекст). Показва се над DialogHeader.
 */
export default function CycleOnboardingModal({ open, onOpenChange, onComplete, stepperSlot }) {
  const { API, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [lmp, setLmp] = useState("");
  const [cycleLength, setCycleLength] = useState(28);
  const [phase, setPhase] = useState(null);
  const [saving, setSaving] = useState(false);

  const derivedPhase = useMemo(() => deriveCyclePhase(lmp, cycleLength), [lmp, cycleLength]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    onOpenChange(false);
  };

  const canProceed =
    (step === 1 && !!lmp)
    || (step === 2 && cycleLength >= 20 && cycleLength <= 45)
    || step === 3;

  const submit = async () => {
    setSaving(true);
    try {
      // 1) LMP + cycle length via /dil/cycle
      await axios.put(`${API}/dil/cycle`, {
        lastPeriodDate: lmp,
        avgCycleLength: cycleLength,
      });
      // 2) Optional phase override
      if (phase) {
        await axios.put(`${API}/dil/cycle`, { cyclePhase: phase });
      }
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
      if (refreshUser) await refreshUser();
      toast.success(bg
        ? "Готово! Hormonal twin е отключен 🎉"
        : "Done! Hormonal twin is unlocked 🎉");
      onOpenChange(false);
      // iter1095 — allow overriding default nav (Register flow)
      if (onComplete) {
        onComplete();
      } else {
        navigate("/dashboard/twin-network");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при запис" : "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const stepTitle = {
    1: bg ? "Кога беше последната Ви менструация?" : "When was your last period?",
    2: bg ? "Каква е средната дължина на цикъла?" : "Average cycle length?",
    3: bg ? "В коя фаза сте сега?" : "Which phase are you in?",
  }[step];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="cycle-onboarding-modal"
        className={`max-w-md w-[95vw] rounded-2xl overflow-hidden ${
          isDark ? "bg-[hsl(222,47%,10%)] border-[hsl(223,47%,18%)] text-slate-100" : "bg-white"
        }`}
      >
        {stepperSlot ? <div className="mb-2">{stepperSlot}</div> : null}
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className={`w-5 h-5 ${isDark ? "text-fuchsia-300" : "text-fuchsia-600"}`} />
            <DialogTitle className="text-base sm:text-lg font-bold">
              {bg ? "Отключете Hormonal twin" : "Unlock your Hormonal twin"}
            </DialogTitle>
          </div>
          <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {bg
              ? "3 бързи стъпки → персонализиран DIL бюджет + hormonal insights."
              : "3 quick steps → personalised DIL budget + hormonal insights."}
          </p>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              data-testid={`cycle-onb-step-indicator-${s}`}
              className={`flex-1 h-1.5 rounded-full transition-all ${
                s <= step
                  ? (isDark ? "bg-gradient-to-r from-fuchsia-500 to-purple-500" : "bg-fuchsia-600")
                  : (isDark ? "bg-white/10" : "bg-slate-200")
              }`}
            />
          ))}
        </div>

        <div className="min-h-[180px]">
          <h3 className={`text-sm font-bold mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>
            {stepTitle}
          </h3>

          {step === 1 && (
            <div data-testid="cycle-onb-step-1">
              <input
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                min={new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString().slice(0, 10)}
                value={lmp}
                onChange={(e) => setLmp(e.target.value)}
                data-testid="cycle-onb-lmp-input"
                className={`w-full text-sm px-3 py-2.5 rounded-xl border ${
                  isDark ? "bg-white/5 border-white/15 text-slate-100" : "bg-white border-slate-300 text-slate-900"
                } focus:outline-none focus:ring-2 focus:ring-fuchsia-400/40`}
              />
              <p className={`text-[11px] mt-2 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {bg
                  ? "Използваме само за оценка на фазата. Не се споделя."
                  : "Used only to estimate your phase. Never shared."}
              </p>
            </div>
          )}

          {step === 2 && (
            <div data-testid="cycle-onb-step-2">
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={20}
                  max={45}
                  value={cycleLength}
                  onChange={(e) => setCycleLength(parseInt(e.target.value, 10))}
                  data-testid="cycle-onb-length-slider"
                  className="flex-1"
                />
                <div className={`text-2xl font-bold w-16 text-center ${isDark ? "text-fuchsia-300" : "text-fuchsia-600"}`}>
                  {cycleLength}
                  <span className={`text-xs block ${isDark ? "text-slate-400" : "text-slate-500"}`}>{bg ? "дни" : "days"}</span>
                </div>
              </div>
              <div className={`grid grid-cols-3 gap-2 mt-3 text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                <button type="button" onClick={() => setCycleLength(26)} className={`py-1.5 rounded ${cycleLength === 26 ? "bg-fuchsia-500/20 text-fuchsia-300" : ""}`}>26</button>
                <button type="button" onClick={() => setCycleLength(28)} className={`py-1.5 rounded ${cycleLength === 28 ? "bg-fuchsia-500/20 text-fuchsia-300" : ""}`}>28 ({bg ? "средно" : "average"})</button>
                <button type="button" onClick={() => setCycleLength(30)} className={`py-1.5 rounded ${cycleLength === 30 ? "bg-fuchsia-500/20 text-fuchsia-300" : ""}`}>30</button>
              </div>
              {derivedPhase && (
                <div
                  data-testid="cycle-onb-derived-preview"
                  className={`mt-3 rounded-lg p-2.5 text-xs ${
                    isDark ? "bg-emerald-500/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                  }`}
                >
                  {bg ? "🔮 От тези данни изчислихме, че сега сте в " : "🔮 We estimate you're currently in "}
                  <strong>{PHASES.find(p => p.key === derivedPhase)?.[bg ? "labelBg" : "labelEn"]}</strong>
                  {bg ? " фаза." : " phase."}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="cycle-onb-step-3">
              <p className={`text-xs mb-2 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {bg
                  ? `Можете да пропуснете — ще използваме автоматичното (${derivedPhase ? PHASES.find(p => p.key === derivedPhase)?.labelBg : "?"}).`
                  : `Optional — we'll auto-derive if you skip (${derivedPhase || "?"}).`}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {PHASES.map((p) => {
                  const active = phase === p.key || (!phase && derivedPhase === p.key);
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPhase(p.key === phase ? null : p.key)}
                      data-testid={`cycle-onb-phase-${p.key}`}
                      className={`px-3 py-2.5 rounded-xl text-xs font-semibold border transition-all text-left ${
                        active
                          ? (isDark ? `bg-${p.color}-500/20 border-${p.color}-500/50 text-${p.color}-200`
                                    : `bg-${p.color}-100 border-${p.color}-400 text-${p.color}-800`)
                          : (isDark ? "bg-white/5 border-white/10 hover:bg-white/10 text-slate-300"
                                    : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700")
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-base leading-none">{p.emoji}</span>
                        <span>{bg ? p.labelBg : p.labelEn}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-2">
          {step > 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              disabled={saving}
              data-testid="cycle-onb-back"
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold ${
                isDark ? "bg-white/5 hover:bg-white/10 text-slate-300" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
              }`}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {bg ? "Назад" : "Back"}
            </button>
          ) : (
            <button
              type="button"
              onClick={dismiss}
              data-testid="cycle-onb-skip"
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold ${
                isDark ? "bg-transparent hover:bg-white/5 text-slate-400" : "bg-transparent hover:bg-slate-50 text-slate-500"
              }`}
            >
              <X className="w-3.5 h-3.5" /> {bg ? "Не сега" : "Not now"}
            </button>
          )}

          <div className="flex-1" />

          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed || saving}
              data-testid="cycle-onb-next"
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold shadow ${
                canProceed && !saving
                  ? (isDark ? "bg-fuchsia-500 hover:bg-fuchsia-400 text-white" : "bg-fuchsia-600 hover:bg-fuchsia-700 text-white")
                  : (isDark ? "bg-white/10 text-slate-500 cursor-not-allowed" : "bg-slate-200 text-slate-400 cursor-not-allowed")
              }`}
            >
              {bg ? "Напред" : "Next"} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              data-testid="cycle-onb-submit"
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold shadow ${
                !saving
                  ? (isDark ? "bg-emerald-500 hover:bg-emerald-400 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white")
                  : (isDark ? "bg-white/10 text-slate-500" : "bg-slate-200 text-slate-400")
              }`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {saving ? (bg ? "Записва..." : "Saving...") : (bg ? "Отключи twin-a" : "Unlock twin")}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function shouldShowCycleOnboarding(user) {
  if (!user) return false;
  const p = user.profile || {};
  const personal = p.personal || {};
  const gender = String(personal.gender || "").toLowerCase();
  if (!gender.startsWith("f") && gender !== "жена") return false;
  if (p.lastPeriodDate) return false;   // already has data
  try {
    if (localStorage.getItem(STORAGE_KEY) === "1") return false;
  } catch { /* noop */ }
  return true;
}
